import { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react'
import { useProjectContext } from '../../context/ProjectContext'
import GridCell from './GridCell'
import GridOverlay from './GridOverlay'
import './DesignCanvas.css'
import StitchActionPad from './StitchActionPad'

const CELL_WIDTH = 11
const CELL_HEIGHT = 18

const DesignCanvas = () => {
  const {
    project,
    initializeGrid,
    hasLoadedProject,
    selectedColor,
    selectedTool,
    selectedDirection,
    selectedLoopHeight,
    selectedStitchCell,
    setSelectedStitchCell,
    updateCellsBatch,
    updateCell,
    zoomLevel,
    setZoomLevel,
    zoomIn,
    zoomOut,
    setZoom,
    startPainting,
    stopPainting,
    brushSize,
  } = useProjectContext()

  const [isPainting, setIsPainting] = useState(false)
  const [isPanning, setIsPanning] = useState(false)
  const [hoverCell, setHoverCell] = useState({ row: null, col: null })
  const [draggedStitch, setDraggedStitch] = useState(null)
  const [showMiniMap, setShowMiniMap] = useState(true)
  const [spacePanActive, setSpacePanActive] = useState(false)
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 })
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 })
  const [brushBadgePulseKey, setBrushBadgePulseKey] = useState(0)
  const [showBrushBadge, setShowBrushBadge] = useState(false)
  const [stitchActionPadPos, setStitchActionPadPos] = useState({ top: 16, left: 16 })
  const [manualPadPos, setManualPadPos] = useState(null)
  const [isFabricPreview, setIsFabricPreview] = useState(false)
  const [cursorPreview, setCursorPreview] = useState({
    visible: false,
    x: 0,
    y: 0,
  })
  const [pendingDrag, setPendingDrag] = useState(null)

  const panStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 })
  const padDragRef = useRef({ active: false, offsetX: 0, offsetY: 0 })
  const isPanningRef = useRef(false)
  const isPaintingRef = useRef(false)
  const isDraggingStitchRef = useRef(false)
  const designCanvasRef = useRef(null)
  const firstRowRef = useRef(null)
  const canvasContainerRef = useRef(null)

  const [rulerMetrics, setRulerMetrics] = useState({
    gridWidth: 0,
    gridHeight: 0,
    colPitch: CELL_WIDTH,
    rowPitch: CELL_HEIGHT,
  })

  const stitchPreviewEnabled =
    !isFabricPreview &&
    selectedTool === 'place-stitch' &&
    selectedDirection &&
    selectedDirection !== 'none'

  const getStitchPreviewRotation = (direction) => {
    const dir = String(direction || 'up').toLowerCase().replace(/\s+/g, '-')

    switch (dir) {
      case 'up':
      case 'vertical':
      case 'none':
        return 0
      case 'down':
        return 180
      case 'left':
        return -90
      case 'right':
      case 'horizontal':
        return 90
      case 'up-right':
      case 'upright':
      case 'right-up':
        return 45
      case 'up-left':
      case 'upleft':
      case 'left-up':
        return -45
      case 'down-right':
      case 'downright':
      case 'right-down':
        return 135
      case 'down-left':
      case 'downleft':
      case 'left-down':
      case 'diagonal-left':
        return -135
      case 'diagonal-right':
        return 135
      default:
        return 0
    }
  }

  const getSafeLoopHeight = () => '16px'

  const getPaletteHex = (colorId) => {
    return (
      project.palette?.find?.((p) => String(p?.code) === String(colorId))?.hex ||
      project.palette?.find?.((p) => String(p?.code) === String(colorId))?.color ||
      '#497fd2'
    )
  }

  const getGridCellFromPointer = (clientX, clientY) => {
    if (!designCanvasRef.current) return null

    const canvasRect = designCanvasRef.current.getBoundingClientRect()
    const adjustedX = (clientX - canvasRect.left) / zoomLevel
    const adjustedY = (clientY - canvasRect.top) / zoomLevel

    const rowCount = project.grid.length
    const colCount = project.grid[0]?.length || 0

    const col = Math.floor(adjustedX / CELL_WIDTH)
    const row = Math.floor(adjustedY / CELL_HEIGHT)

    const inBounds =
      row >= 0 &&
      col >= 0 &&
      row < rowCount &&
      col < colCount

    if (!inBounds) return null

    return {
      row,
      col,
      snappedClientX:
        canvasRect.left + (col * CELL_WIDTH + CELL_WIDTH / 2) * zoomLevel,
      snappedClientY:
        canvasRect.top + (row * CELL_HEIGHT + CELL_HEIGHT / 2) * zoomLevel,
    }
  }

  const getOffsetFromPointer = (clientX, clientY, row, col) => {
    if (!designCanvasRef.current) return null

    const canvasRect = designCanvasRef.current.getBoundingClientRect()
    const adjustedX = (clientX - canvasRect.left) / zoomLevel
    const adjustedY = (clientY - canvasRect.top) / zoomLevel

    const cellCenterX = col * CELL_WIDTH + CELL_WIDTH / 2
    const cellCenterY = row * CELL_HEIGHT + CELL_HEIGHT / 2

    return {
      offsetX: adjustedX - cellCenterX,
      offsetY: adjustedY - cellCenterY,
    }
  }

  const findVisibleStitchAtPointer = (clientX, clientY) => {
    const rowCount = project.grid.length
    const colCount = project.grid[0]?.length || 0

    if (typeof clientX !== 'number' || typeof clientY !== 'number') return null

    const canvasRect = designCanvasRef.current?.getBoundingClientRect()
    if (!canvasRect) return null

    const adjustedX = (clientX - canvasRect.left) / zoomLevel
    const adjustedY = (clientY - canvasRect.top) / zoomLevel

    let bestMatch = null
    let bestDistance = Infinity

    for (let r = 0; r < rowCount; r++) {
      for (let c = 0; c < colCount; c++) {
        const cell = project.grid?.[r]?.[c]
        if (!cell?.colorId) continue

        const offsetX = Number(cell.offsetX) || 0
        const offsetY = Number(cell.offsetY) || 0

        const centerX = c * CELL_WIDTH + CELL_WIDTH / 2 + offsetX
        const centerY = r * CELL_HEIGHT + CELL_HEIGHT / 2 + offsetY

        const dx = adjustedX - centerX
        const dy = adjustedY - centerY
        const dist = Math.sqrt(dx * dx + dy * dy)

        if (dist < bestDistance) {
          bestDistance = dist
          bestMatch = { row: r, col: c }
        }
      }
    }

    const hitRadius = 14
    return bestDistance <= hitRadius ? bestMatch : null
  }

  const findNearestVisibleStitch = (row, col, clientX = null, clientY = null) => {
    if (typeof clientX === 'number' && typeof clientY === 'number') {
      const pointerHit = findVisibleStitchAtPointer(clientX, clientY)
      if (pointerHit) return pointerHit
    }

    const directCell = project.grid?.[row]?.[col]
    if (directCell?.colorId) {
      return { row, col }
    }

    let bestMatch = null
    let bestDistance = Infinity

    for (let r = 0; r < project.grid.length; r++) {
      for (let c = 0; c < (project.grid[0]?.length || 0); c++) {
        const cell = project.grid?.[r]?.[c]
        if (!cell?.colorId) continue

        const dr = r - row
        const dc = c - col
        const dist = Math.sqrt(dr * dr + dc * dc)

        if (dist < bestDistance) {
          bestDistance = dist
          bestMatch = { row: r, col: c }
        }
      }
    }

    return bestDistance <= 1.5 ? bestMatch : null
  }

  const updateStitchCursorPreview = (e) => {
    if (!stitchPreviewEnabled) {
      setCursorPreview((prev) => ({ ...prev, visible: false }))
      return
    }

    const rect = e.currentTarget.getBoundingClientRect()

    setCursorPreview({
      visible: true,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    })
  }

  const hideStitchCursorPreview = () => {
    setCursorPreview((prev) => ({ ...prev, visible: false }))
  }

  useEffect(() => {
    if (!hasLoadedProject) return
    if (project.grid.length === 0) {
      initializeGrid()
    }
  }, [project.grid.length, initializeGrid, hasLoadedProject])

  useEffect(() => {
    setBrushBadgePulseKey((prev) => prev + 1)
  }, [brushSize])

  useEffect(() => {
    if (isFabricPreview) {
      setShowBrushBadge(false)
      setHoverCell({ row: null, col: null })
      hideStitchCursorPreview()
      isPaintingRef.current = false
      setIsPainting(false)
      stopPainting()
    }
  }, [isFabricPreview, stopPainting])

  useLayoutEffect(() => {
    const measureGrid = () => {
      const rows = project.grid.length
      const cols = project.grid[0]?.length || 0

      if (!rows || !cols || !designCanvasRef.current || !firstRowRef.current) return

      const gridWidth = firstRowRef.current.offsetWidth
      const gridHeight = designCanvasRef.current.offsetHeight
      const colPitch = gridWidth / cols
      const rowPitch = gridHeight / rows

      setRulerMetrics({
        gridWidth,
        gridHeight,
        colPitch,
        rowPitch,
      })
    }

    measureGrid()
    window.addEventListener('resize', measureGrid)

    return () => {
      window.removeEventListener('resize', measureGrid)
    }
  }, [project.grid, isFabricPreview])

  useEffect(() => {
    setPanOffset({ x: 0, y: 0 })
  }, [project.grid.length, project.grid[0]?.length])

  useEffect(() => {
    if (!selectedStitchCell) return
    if (manualPadPos) return
    if (!canvasContainerRef.current) return
    if (isFabricPreview) return

    const selectedEl = canvasContainerRef.current.querySelector(
      `[data-cell="${selectedStitchCell.row}-${selectedStitchCell.col}"]`
    )

    if (!selectedEl) return

    const cellRect = selectedEl.getBoundingClientRect()
    const containerRect = canvasContainerRef.current.getBoundingClientRect()

    const estimatedPadWidth = 210
    const estimatedPadHeight = 98
    const gap = 14

    let left = cellRect.right - containerRect.left + gap
    let top =
      cellRect.top -
      containerRect.top +
      cellRect.height / 2 -
      estimatedPadHeight / 2

    if (left + estimatedPadWidth > containerRect.width - 12) {
      left = cellRect.left - containerRect.left - estimatedPadWidth - gap
    }

    if (left < 12) {
      left = 12
    }

    if (top < 12) {
      top = 12
    }

    if (top + estimatedPadHeight > containerRect.height - 12) {
      top = containerRect.height - estimatedPadHeight - 12
    }

    setStitchActionPadPos({ top, left })
  }, [selectedStitchCell, zoomLevel, isFabricPreview, manualPadPos])

  useEffect(() => {
    if (selectedTool !== 'select-stitch' && selectedStitchCell) {
      setSelectedStitchCell(null)
    }
  }, [selectedTool, selectedStitchCell, setSelectedStitchCell])

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code !== 'Space') return
      if (e.repeat) return

      const tag = document.activeElement?.tagName
      const isTyping =
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        document.activeElement?.isContentEditable

      if (isTyping) return

      e.preventDefault()
      setSpacePanActive(true)
    }

    const handleKeyUp = (e) => {
      if (e.code !== 'Space') return
      setSpacePanActive(false)
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [])

  const moveSelectedStitchBy = (rowDelta, colDelta) => {
    if (!selectedStitchCell) return

    if (!manualPadPos) {
      setManualPadPos({
        top: stitchActionPadPos.top,
        left: stitchActionPadPos.left,
      })
    }

    const sourceRow = selectedStitchCell.row
    const sourceCol = selectedStitchCell.col
    const targetRow = sourceRow + rowDelta
    const targetCol = sourceCol + colDelta

    if (targetRow < 0 || targetCol < 0) return
    if (targetRow >= project.grid.length) return
    if (!project.grid[0] || targetCol >= project.grid[0].length) return

    const sourceCell = project.grid?.[sourceRow]?.[sourceCol]
    if (!sourceCell?.colorId) {
      setSelectedStitchCell(null)
      return
    }

    updateCellsBatch([
      {
        row: targetRow,
        col: targetCol,
        updates: {
          colorId: sourceCell.colorId,
          direction: sourceCell.direction,
          loopHeight: sourceCell.loopHeight,
          offsetX: sourceCell.offsetX || 0,
          offsetY: sourceCell.offsetY || 0,
        },
      },
      {
        row: sourceRow,
        col: sourceCol,
        updates: {
          colorId: null,
          direction: 'none',
          loopHeight: 'medium',
          offsetX: 0,
          offsetY: 0,
        },
      },
    ])

    setSelectedStitchCell({ row: targetRow, col: targetCol })
  }

  const duplicateSelectedStitchBy = (rowDelta, colDelta) => {
    if (!selectedStitchCell) return

    if (!manualPadPos) {
      setManualPadPos({
        top: stitchActionPadPos.top,
        left: stitchActionPadPos.left,
      })
    }

    const sourceRow = selectedStitchCell.row
    const sourceCol = selectedStitchCell.col
    const targetRow = sourceRow + rowDelta
    const targetCol = sourceCol + colDelta

    if (targetRow < 0 || targetCol < 0) return
    if (targetRow >= project.grid.length) return
    if (!project.grid[0] || targetCol >= project.grid[0].length) return

    const sourceCell = project.grid?.[sourceRow]?.[sourceCol]
    if (!sourceCell?.colorId) {
      setSelectedStitchCell(null)
      return
    }

    updateCellsBatch([
      {
        row: targetRow,
        col: targetCol,
        updates: {
          colorId: sourceCell.colorId,
          direction: sourceCell.direction,
          loopHeight: sourceCell.loopHeight,
          offsetX: sourceCell.offsetX || 0,
          offsetY: sourceCell.offsetY || 0,
        },
      },
    ])

    setSelectedStitchCell({ row: targetRow, col: targetCol })
  }

  const handleMoveUp = () => moveSelectedStitchBy(-1, 0)
  const handleMoveDown = () => moveSelectedStitchBy(1, 0)
  const handleMoveLeft = () => moveSelectedStitchBy(0, -1)
  const handleMoveRight = () => moveSelectedStitchBy(0, 1)

  const handleDuplicateUp = () => duplicateSelectedStitchBy(-1, 0)
  const handleDuplicateDown = () => duplicateSelectedStitchBy(1, 0)
  const handleDuplicateLeft = () => duplicateSelectedStitchBy(0, -1)
  const handleDuplicateRight = () => duplicateSelectedStitchBy(0, 1)

  const handleDeleteSelectedStitch = () => {
    if (!selectedStitchCell) return

    updateCellsBatch([
      {
        row: selectedStitchCell.row,
        col: selectedStitchCell.col,
        updates: {
          colorId: null,
          direction: 'none',
          loopHeight: 'medium',
          offsetX: 0,
          offsetY: 0,
        },
      },
    ])

    setManualPadPos(null)
    setSelectedStitchCell(null)
  }

  const handleCloseSelectedStitch = () => {
    setManualPadPos(null)
    setSelectedStitchCell(null)
  }

  const handlePadGrabStart = (e) => {
    if (!canvasContainerRef.current) return

    e.preventDefault()
    e.stopPropagation()

    const containerRect = canvasContainerRef.current.getBoundingClientRect()
    const currentTop = manualPadPos?.top ?? stitchActionPadPos.top
    const currentLeft = manualPadPos?.left ?? stitchActionPadPos.left

    padDragRef.current = {
      active: true,
      offsetX: e.clientX - (containerRect.left + currentLeft),
      offsetY: e.clientY - (containerRect.top + currentTop),
    }
  }

  useEffect(() => {
    const handleSelectedStitchKeys = (e) => {
      const tag = document.activeElement?.tagName
      const isTyping =
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        document.activeElement?.isContentEditable

      if (isTyping) return
      if (!selectedStitchCell) return
      if (isFabricPreview) return

      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault()
        updateCellsBatch([
          {
            row: selectedStitchCell.row,
            col: selectedStitchCell.col,
            updates: {
              colorId: null,
              direction: 'none',
              loopHeight: 'medium',
              offsetX: 0,
              offsetY: 0,
            },
          },
        ])
        setSelectedStitchCell(null)
        return
      }

      const step = e.shiftKey ? 5 : 1
      const useDuplicate = e.altKey

      if (e.key === 'ArrowUp') {
        e.preventDefault()
        if (useDuplicate) duplicateSelectedStitchBy(-step, 0)
        else moveSelectedStitchBy(-step, 0)
        return
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        if (useDuplicate) duplicateSelectedStitchBy(step, 0)
        else moveSelectedStitchBy(step, 0)
        return
      }

      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        if (useDuplicate) duplicateSelectedStitchBy(0, -step)
        else moveSelectedStitchBy(0, -step)
        return
      }

      if (e.key === 'ArrowRight') {
        e.preventDefault()
        if (useDuplicate) duplicateSelectedStitchBy(0, step)
        else moveSelectedStitchBy(0, step)
      }
    }

    window.addEventListener('keydown', handleSelectedStitchKeys)
    return () => window.removeEventListener('keydown', handleSelectedStitchKeys)
  }, [selectedStitchCell, project.grid, updateCellsBatch, setSelectedStitchCell, isFabricPreview])

  useEffect(() => {
    const handlePadPointerMove = (e) => {
      if (!padDragRef.current.active) return
      if (!canvasContainerRef.current) return

      const containerRect = canvasContainerRef.current.getBoundingClientRect()
      const padWidth = 210
      const padHeight = 98

      let left = e.clientX - containerRect.left - padDragRef.current.offsetX
      let top = e.clientY - containerRect.top - padDragRef.current.offsetY

      if (left < 12) left = 12
      if (top < 12) top = 12
      if (left + padWidth > containerRect.width - 12) {
        left = containerRect.width - padWidth - 12
      }
      if (top + padHeight > containerRect.height - 12) {
        top = containerRect.height - padHeight - 12
      }

      setManualPadPos({ top, left })
    }

    const handlePadPointerUp = () => {
      padDragRef.current.active = false
    }

    window.addEventListener('mousemove', handlePadPointerMove)
    window.addEventListener('mouseup', handlePadPointerUp)

    return () => {
      window.removeEventListener('mousemove', handlePadPointerMove)
      window.removeEventListener('mouseup', handlePadPointerUp)
    }
  }, [manualPadPos, stitchActionPadPos])

  const applyAt = useCallback(
    (row, col, e = null) => {
      if (isFabricPreview) return

      if (selectedTool === 'place-stitch') {
        if (!selectedColor || !selectedDirection || selectedDirection === 'none') return

        updateCellsBatch([
          {
            row,
            col,
            updates: {
              colorId: selectedColor.code,
              direction: selectedDirection,
              loopHeight: selectedLoopHeight === 'none' ? 'medium' : selectedLoopHeight,
              offsetX: 0,
              offsetY: 0,
            },
          },
        ])
        return
      }

      const size = Math.max(1, Number(brushSize) || 1)
      const changes = []

      for (let r = row; r < row + size; r++) {
        for (let c = col; c < col + size; c++) {
          if (r < 0 || c < 0) continue
          if (r >= project.grid.length) continue
          if (!project.grid[0] || c >= project.grid[0].length) continue

          if (selectedTool === 'paint' && selectedColor) {
            const existingCell = project.grid?.[r]?.[c]

            changes.push({
              row: r,
              col: c,
              updates: {
                colorId: selectedColor.code,
                direction: existingCell?.colorId
                  ? (existingCell.direction || 'none')
                  : (selectedDirection || 'none'),
                loopHeight: existingCell?.colorId
                  ? (existingCell.loopHeight || 'medium')
                  : (selectedLoopHeight === 'none' ? 'medium' : selectedLoopHeight),
                offsetX: existingCell?.colorId ? (existingCell.offsetX || 0) : 0,
                offsetY: existingCell?.colorId ? (existingCell.offsetY || 0) : 0,
              },
            })
          } else if (selectedTool === 'erase') {
            const target = findVisibleStitchAtPointer(e?.clientX, e?.clientY)

            if (target) {
              const alreadyQueued = changes.some(
                (change) => change.row === target.row && change.col === target.col
              )

              if (!alreadyQueued) {
                changes.push({
                  row: target.row,
                  col: target.col,
                  updates: {
                    colorId: null,
                    direction: 'none',
                    loopHeight: 'medium',
                    offsetX: 0,
                    offsetY: 0,
                  },
                })
              }
            }
          } else if (selectedTool === 'direction') {
            const currentCell = project.grid?.[r]?.[c]
            if (!currentCell?.colorId) continue

            changes.push({
              row: r,
              col: c,
              updates: {
                direction: selectedDirection,
              },
            })
          } else if (selectedTool === 'loopHeight') {
            const currentCell = project.grid?.[r]?.[c]
            if (!currentCell?.colorId) continue

            changes.push({
              row: r,
              col: c,
              updates: {
                loopHeight: selectedLoopHeight === 'none' ? 'medium' : selectedLoopHeight,
              },
            })
          }
        }
      }

      if (changes.length === 0) return
      updateCellsBatch(changes)
    },
    [
      brushSize,
      isFabricPreview,
      project.grid,
      selectedTool,
      selectedColor,
      selectedDirection,
      selectedLoopHeight,
      updateCellsBatch,
    ]
  )

  const activePanMode = !isFabricPreview && (selectedTool === 'hand' || spacePanActive)

  const handleMouseDown = (row, col, e) => {
    if (activePanMode) return
    if (isFabricPreview) return

    if (selectedTool === 'select-stitch') {
      const target = findVisibleStitchAtPointer(e?.clientX, e?.clientY)

      if (!target) {
        setManualPadPos(null)
        setSelectedStitchCell(null)
        return
      }

      const isSame =
        selectedStitchCell?.row === target.row &&
        selectedStitchCell?.col === target.col

      if (!isSame) {
        setManualPadPos(null)
      }

      setPendingDrag({
        row: target.row,
        col: target.col,
        startX: e.clientX,
        startY: e.clientY,
        wasSelected: isSame,
      })

      return
    }

    if (selectedTool === 'erase') {
      const target = findVisibleStitchAtPointer(e?.clientX, e?.clientY)

      if (target) {
        updateCell(target.row, target.col, {
          colorId: null,
          direction: 'none',
          loopHeight: 'medium',
          offsetX: 0,
          offsetY: 0,
        })
      }
      return
    }

    if (selectedTool === 'place-stitch') {
      if (!selectedColor || !selectedDirection || selectedDirection === 'none') return

      const loopValue = selectedLoopHeight === 'none' ? 'medium' : selectedLoopHeight

      updateCell(row, col, {
        colorId: selectedColor.code,
        direction: selectedDirection,
        loopHeight: loopValue,
        offsetX: 0,
        offsetY: 0,
      })

      isDraggingStitchRef.current = true
      setSelectedStitchCell({ row, col })
      setDraggedStitch({
        colorId: selectedColor.code,
        direction: selectedDirection,
        loopHeight: loopValue,
        offsetX: 0,
        offsetY: 0,
        row,
        col,
        x: e?.clientX ?? cursorPos.x,
        y: e?.clientY ?? cursorPos.y,
        dragStartX: e?.clientX ?? cursorPos.x,
        dragStartY: e?.clientY ?? cursorPos.y,
        wasAlreadySelected: false,
      })
      setPendingDrag(null)
      return
    }

    isPaintingRef.current = true
    setIsPainting(true)
    startPainting()
    applyAt(row, col, e)
  }

  const handleMouseEnter = (row, col, e) => {
    if (selectedTool === 'place-stitch') return
    if (activePanMode) return
    if (isFabricPreview) return

    setHoverCell({ row, col })

    const primaryDown = e?.buttons === 1

    if (selectedTool === 'place-stitch' || selectedTool === 'select-stitch') return

    if (isPaintingRef.current || primaryDown) {
      if (primaryDown && !isPaintingRef.current) {
        isPaintingRef.current = true
        setIsPainting(true)
      }
      applyAt(row, col, e)
    }
  }

  const handleMouseLeaveCanvas = () => {
    setHoverCell({ row: null, col: null })
    setShowBrushBadge(false)
    hideStitchCursorPreview()

    if (isPaintingRef.current || isPainting) {
      isPaintingRef.current = false
      setIsPainting(false)
      stopPainting()
    }
  }

  const handleMouseUp = (clientX = null, clientY = null) => {
    isPaintingRef.current = false
    setIsPainting(false)
    stopPainting()

    if (pendingDrag && !draggedStitch) {
      const { row, col, wasSelected } = pendingDrag

      if (wasSelected) {
        setSelectedStitchCell(null)
      } else {
        setSelectedStitchCell({ row, col })
      }

      setPendingDrag(null)
      return
    }

    if (!draggedStitch) {
      setDraggedStitch(null)
      return
    }

    if (typeof clientX !== 'number' || typeof clientY !== 'number') {
      setDraggedStitch(null)
      isDraggingStitchRef.current = false
      return
    }

    const moveX = clientX - (draggedStitch.dragStartX ?? clientX)
    const moveY = clientY - (draggedStitch.dragStartY ?? clientY)
    const moveDistance = Math.sqrt(moveX * moveX + moveY * moveY)
    const wasClick = moveDistance < 4

    if (wasClick) {
      if (draggedStitch.wasAlreadySelected) {
        setSelectedStitchCell(null)
      } else {
        setSelectedStitchCell({
          row: draggedStitch.row,
          col: draggedStitch.col,
        })
      }

      setDraggedStitch(null)

      window.setTimeout(() => {
        isDraggingStitchRef.current = false
      }, 0)

      return
    }

    const sourceRow = draggedStitch.row
    const sourceCol = draggedStitch.col
    const target = getGridCellFromPointer(clientX, clientY)

    if (!target) {
      setDraggedStitch(null)

      window.setTimeout(() => {
        isDraggingStitchRef.current = false
      }, 0)

      return
    }

    const offsets = getOffsetFromPointer(clientX, clientY, target.row, target.col)

    if (!offsets) {
      setDraggedStitch(null)

      window.setTimeout(() => {
        isDraggingStitchRef.current = false
      }, 0)

      return
    }

    const sameCell = sourceRow === target.row && sourceCol === target.col

    if (sameCell) {
      updateCellsBatch([
        {
          row: sourceRow,
          col: sourceCol,
          updates: {
            colorId: draggedStitch.colorId,
            direction: draggedStitch.direction,
            loopHeight: draggedStitch.loopHeight || 'medium',
            offsetX: offsets.offsetX,
            offsetY: offsets.offsetY,
          },
        },
      ])

      setSelectedStitchCell({ row: sourceRow, col: sourceCol })
    } else {
      updateCellsBatch([
        {
          row: target.row,
          col: target.col,
          updates: {
            colorId: draggedStitch.colorId,
            direction: draggedStitch.direction,
            loopHeight: draggedStitch.loopHeight || 'medium',
            offsetX: offsets.offsetX,
            offsetY: offsets.offsetY,
          },
        },
        {
          row: sourceRow,
          col: sourceCol,
          updates: {
            colorId: null,
            direction: 'none',
            loopHeight: 'medium',
            offsetX: 0,
            offsetY: 0,
          },
        },
      ])

      setSelectedStitchCell({ row: target.row, col: target.col })
    }

    setDraggedStitch(null)

    window.setTimeout(() => {
      isDraggingStitchRef.current = false
    }, 0)
  }

  const handleClearCell = (row, col) => {
    updateCell(row, col, {
      colorId: null,
      direction: 'none',
      loopHeight: 'medium',
      offsetX: 0,
      offsetY: 0,
    })
  }

  const handleCenterCanvas = () => {
    setPanOffset({ x: 0, y: 0 })
  }

  const handleActualSize = () => {
    setZoom(1)
    setPanOffset({ x: 0, y: 0 })
  }

  const handlePanStart = (e) => {
    if (!activePanMode) return

    e.preventDefault()

    isPanningRef.current = true
    setIsPanning(true)

    panStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      offsetX: panOffset.x,
      offsetY: panOffset.y,
    }
  }

  const handlePanMove = (e) => {
    if (!activePanMode || !isPanningRef.current) return

    e.preventDefault()

    const dx = e.clientX - panStartRef.current.x
    const dy = e.clientY - panStartRef.current.y

    setPanOffset({
      x: panStartRef.current.offsetX + dx,
      y: panStartRef.current.offsetY + dy,
    })
  }

  const handlePanEnd = () => {
    isPanningRef.current = false
    setIsPanning(false)
  }

  const handleStartDrag = (cell, rowIndex, colIndex, e, wasAlreadySelected = false) => {
    if (selectedTool !== 'select-stitch') return
    if (!cell?.colorId) return

    isDraggingStitchRef.current = true
    setSelectedStitchCell({ row: rowIndex, col: colIndex })

    setDraggedStitch({
      ...cell,
      row: rowIndex,
      col: colIndex,
      x: e?.clientX ?? cursorPos.x,
      y: e?.clientY ?? cursorPos.y,
      dragStartX: e?.clientX ?? cursorPos.x,
      dragStartY: e?.clientY ?? cursorPos.y,
      wasAlreadySelected,
    })
  }

  const renderDraggedStitch = () => {
    if (!draggedStitch) return null
    if (!Number.isFinite(draggedStitch.x) || !Number.isFinite(draggedStitch.y)) return null

    return (
      <div
        className="floating-stitch-overlay"
        style={{
          position: 'fixed',
          left: `${draggedStitch.x}px`,
          top: `${draggedStitch.y}px`,
          transform: `translate(-50%, -50%) rotate(${getStitchPreviewRotation(
            draggedStitch.direction
          )}deg)`,
          pointerEvents: 'none',
          zIndex: 999999,
        }}
      >
        <div
          className="floating-stitch-shape"
          style={{
            backgroundColor: getPaletteHex(draggedStitch.colorId),
            height: getSafeLoopHeight(draggedStitch.loopHeight),
          }}
        />
      </div>
    )
  }

  if (project.grid.length === 0) {
    return (
      <div className="canvas-container">
        <div className="canvas-placeholder">
          <p>Initializing canvas...</p>
        </div>
      </div>
    )
  }

  const size = Math.max(1, Number(brushSize) || 1)

  const previewActive =
    !isFabricPreview &&
    hoverCell.row !== null &&
    hoverCell.col !== null &&
    ((selectedTool === 'paint' && !!selectedColor) || selectedTool === 'erase')

  const rows = project.grid.length
  const cols = project.grid[0]?.length || 0
  const rulerSize = 28
  const spi = Math.max(1, Math.round(Number(project.stitchesPerInch) || 1))
  const halfSpi = Math.max(1, Math.round(spi / 2))
  const gridWidth = rulerMetrics.gridWidth || cols * CELL_WIDTH
  const gridHeight = rulerMetrics.gridHeight || rows * CELL_HEIGHT
  const miniMapCols = cols
  const miniMapRows = rows
  const miniMapCellSize = Math.max(2, Math.floor(140 / Math.max(miniMapCols, miniMapRows, 1)))
  const viewportMiniWidth = Math.max(12, 60 / zoomLevel)
  const viewportMiniHeight = Math.max(12, 60 / zoomLevel)
  const viewportMiniLeft = Math.max(0, (-panOffset.x / CELL_WIDTH) * miniMapCellSize)
  const viewportMiniTop = Math.max(0, (-panOffset.y / CELL_HEIGHT) * miniMapCellSize)

  const handleFitGrid = () => {
    if (!canvasContainerRef.current) return

    const containerRect = canvasContainerRef.current.getBoundingClientRect()
    const toolbarAllowance = 140
    const sidePadding = 80
    const availableWidth = Math.max(100, containerRect.width - sidePadding)
    const availableHeight = Math.max(100, containerRect.height - toolbarAllowance)
    const fullGridWidth = isFabricPreview ? gridWidth : rulerSize + gridWidth + rulerSize
    const fullGridHeight = isFabricPreview ? gridHeight : rulerSize + gridHeight + rulerSize

    if (!fullGridWidth || !fullGridHeight) return

    const fitZoomX = availableWidth / fullGridWidth
    const fitZoomY = availableHeight / fullGridHeight
    const nextZoom = Math.max(0.5, Math.min(4, Number(Math.min(fitZoomX, fitZoomY).toFixed(2))))

    setZoomLevel(nextZoom)
    setPanOffset({ x: 0, y: 0 })
  }

  const handleMiniMapClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const clickX = e.clientX - rect.left
    const clickY = e.clientY - rect.top
    const totalMiniWidth = miniMapCols * miniMapCellSize
    const totalMiniHeight = miniMapRows * miniMapCellSize
    const targetGridX = (clickX / totalMiniWidth) * (cols * CELL_WIDTH)
    const targetGridY = (clickY / totalMiniHeight) * (rows * CELL_HEIGHT)
    const visibleWidth = 240 / zoomLevel
    const visibleHeight = 240 / zoomLevel

    setPanOffset({
      x: -(targetGridX - visibleWidth / 2),
      y: -(targetGridY - visibleHeight / 2),
    })
  }

  const getBrushBadgeLabel = () => {
    if (activePanMode) return '✋ Pan'
    if (selectedTool === 'erase') return `🧽 ${brushSize}×${brushSize}`
    if (selectedTool === 'paint') return `🖌 ${brushSize}×${brushSize}`
    if (selectedTool === 'direction') return '↔ Direction'
    if (selectedTool === 'loopHeight') return '〰 Loop'
    if (selectedTool === 'place-stitch') return '🪡 Stitch'
    if (selectedTool === 'select-stitch') return '🖱️ Select'
    return `${brushSize}×${brushSize}`
  }

  const getBrushBadgeColor = () => {
    if (activePanMode) return 'rgba(60, 60, 60, 0.9)'
    if (selectedTool === 'erase') return 'rgba(220, 38, 38, 0.9)'
    if (selectedTool === 'paint') return 'rgba(37, 99, 235, 0.9)'
    if (selectedTool === 'direction') return 'rgba(139, 92, 246, 0.9)'
    if (selectedTool === 'loopHeight') return 'rgba(245, 158, 11, 0.9)'
    if (selectedTool === 'place-stitch') return 'rgba(16, 185, 129, 0.9)'
    if (selectedTool === 'select-stitch') return 'rgba(73, 127, 210, 0.9)'
    return 'rgba(40, 40, 40, 0.85)'
  }

  return (
    <>
      <div
        ref={canvasContainerRef}
        className={`canvas-container ${activePanMode ? 'hand-mode' : ''} ${isPanning ? 'is-panning' : ''}`}
        onPointerDownCapture={!isFabricPreview ? handlePanStart : undefined}
        onPointerMove={!isFabricPreview ? handlePanMove : undefined}
        onPointerUp={(e) => {
          if (!isFabricPreview) {
            handlePanEnd()
            handleMouseUp(e.clientX, e.clientY)
          }
        }}
        onPointerLeave={() => {
          handlePanEnd()
          handleMouseLeaveCanvas()
        }}
        onPointerCancel={!isFabricPreview ? handlePanEnd : undefined}
      >
        <div className="canvas-toolbar">
          <div className="mode-toggle">
            <button
              type="button"
              className={!isFabricPreview ? 'active' : ''}
              onClick={() => {
                setPanOffset({ x: 0, y: 0 })
                setIsFabricPreview(false)
              }}
            >
              Design Mode
            </button>

            <button
              type="button"
              className={isFabricPreview ? 'active' : ''}
              onClick={() => {
                setPanOffset({ x: 0, y: 0 })
                setIsFabricPreview(true)
              }}
            >
              Fabric Preview
            </button>
          </div>

          <div className="tool-status" aria-live="polite">
            {selectedTool === 'paint' ? (
              selectedColor ? (
                <span className="status-pill status-armed">
                  <span className="color-swatch" style={{ backgroundColor: selectedColor?.hex }} />
                  Paint: Armed
                </span>
              ) : (
                <span className="status-pill status-waiting">Paint: Select color</span>
              )
            ) : selectedTool === 'place-stitch' ? (
              selectedColor ? (
                <span className="status-pill status-armed">
                  <span className="color-swatch" style={{ backgroundColor: selectedColor?.hex }} />
                  Stitch: Armed
                </span>
              ) : (
                <span className="status-pill status-waiting">Stitch: Select color</span>
              )
            ) : selectedTool === 'select-stitch' ? (
              <span className="status-pill status-muted">
                Select: {selectedStitchCell ? 'Stitch selected' : 'Click a stitch'}
              </span>
            ) : (
              <span className="status-pill status-muted">
                Tool: {selectedTool || '—'}
              </span>
            )}
          </div>

          <div className="zoom-controls canvas-zoom-controls">
            <button type="button" onClick={zoomOut}>−</button>
            <input
              type="range"
              min="0.5"
              max="4"
              step="0.1"
              value={zoomLevel}
              onChange={(e) => setZoom(Number(e.target.value))}
            />
            <button type="button" onClick={zoomIn}>+</button>
            <button type="button" onClick={handleActualSize}>100%</button>
            <button type="button" onClick={handleFitGrid}>Fit Grid</button>
          </div>

          <button type="button" className="view-reset-button" onClick={handleCenterCanvas}>
            Center
          </button>

          {!isFabricPreview && (
            <button
              type="button"
              className="view-reset-button"
              onClick={() => setShowMiniMap((prev) => !prev)}
            >
              {showMiniMap ? 'Hide MiniMap' : 'Show MiniMap'}
            </button>
          )}
        </div>

        <div
          className={`canvas-wrapper ${isFabricPreview ? 'fabric-preview-mode' : ''}`}
          style={{
            transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
            touchAction: selectedTool === 'hand' ? 'none' : 'auto',
            userSelect: selectedTool === 'hand' ? 'none' : 'auto',
          }}
        >
          <div
            className={`ruler-frame ${isFabricPreview ? 'fabric-preview-mode' : ''}`}
            style={{
              gridTemplateColumns: isFabricPreview
                ? `${gridWidth}px`
                : `${rulerSize}px ${gridWidth}px ${rulerSize}px`,
              gridTemplateRows: isFabricPreview
                ? `${gridHeight}px`
                : `${rulerSize}px ${gridHeight}px ${rulerSize}px`,
            }}
          >
            {!isFabricPreview && <div className="ruler-corner">0</div>}

            {!isFabricPreview && (
              <div className="top-ruler" style={{ width: `${gridWidth}px` }}>
                {Array.from({ length: cols + 1 }).map((_, index) => {
                  const percent = (index / cols) * 100
                  const tickClass =
                    index % spi === 0 ? 'major' : index % halfSpi === 0 ? 'mid' : 'minor'

                  return (
                    <div
                      key={`top-${index}`}
                      className={`ruler-tick top ${tickClass}`}
                      style={{ left: `${percent}%` }}
                    >
                      {index % spi === 0 ? (
                        <span className="ruler-label">{index / spi}</span>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            )}

            {!isFabricPreview && <div className="ruler-corner" />}

            {!isFabricPreview && (
              <div className="left-ruler" style={{ height: `${gridHeight}px` }}>
                {Array.from({ length: rows + 1 }).map((_, index) => {
                  const percent = (index / rows) * 100
                  const tickClass =
                    index % spi === 0 ? 'major' : index % halfSpi === 0 ? 'mid' : 'minor'

                  return (
                    <div
                      key={`left-${index}`}
                      className={`ruler-tick left ${tickClass}`}
                      style={{ top: `${percent}%` }}
                    >
                      {index % spi === 0 ? (
                        <span className="ruler-label">{index / spi}</span>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            )}

            <div
              ref={designCanvasRef}
              className={`design-canvas ${isFabricPreview ? 'fabric-preview-mode' : ''}`}
              onMouseEnter={() => {
                if (!isFabricPreview) {
                  setShowBrushBadge(true)
                }
              }}
              onMouseMove={(e) => {
                if (pendingDrag && !draggedStitch) {
                  const dx = e.clientX - pendingDrag.startX
                  const dy = e.clientY - pendingDrag.startY
                  const dist = Math.sqrt(dx * dx + dy * dy)

                  if (dist > 5) {
                    const cell = project.grid?.[pendingDrag.row]?.[pendingDrag.col]

                    if (cell?.colorId) {
                      handleStartDrag(
                        cell,
                        pendingDrag.row,
                        pendingDrag.col,
                        e,
                        pendingDrag.wasSelected
                      )
                    }

                    setPendingDrag(null)
                  }

                  return
                }

                if (draggedStitch) {
                  setDraggedStitch((prev) => ({
                    ...prev,
                    x: e.clientX,
                    y: e.clientY,
                  }))
                  return
                }

                if (!isFabricPreview) {
                  setCursorPos({
                    x: e.clientX,
                    y: e.clientY,
                  })
                  setShowBrushBadge(true)
                }

                updateStitchCursorPreview(e)
              }}
              onMouseLeave={() => {
                setHoverCell({ row: null, col: null })
                setShowBrushBadge(false)
                hideStitchCursorPreview()
              }}
            >
              {project.grid.map((rowArr, rowIndex) => (
                <div
                  key={rowIndex}
                  className="canvas-row"
                  ref={rowIndex === 0 ? firstRowRef : null}
                >
                  {rowArr.map((cell, colIndex) => {
                    const previewSize = size
                    const hasHoverCell = hoverCell.row !== null && hoverCell.col !== null
                    const allowGridPreview = selectedTool !== 'place-stitch'

                    const inBrushPreview =
                      allowGridPreview &&
                      !isFabricPreview &&
                      hasHoverCell &&
                      previewActive &&
                      rowIndex >= hoverCell.row &&
                      rowIndex < hoverCell.row + previewSize &&
                      colIndex >= hoverCell.col &&
                      colIndex < hoverCell.col + previewSize &&
                      (selectedTool === 'erase' || !cell?.colorId)

                    return (
                      <GridCell
                        key={`${rowIndex}-${colIndex}`}
                        row={rowIndex}
                        col={colIndex}
                        cell={cell}
                        dataCell={`${rowIndex}-${colIndex}`}
                        onMouseDown={handleMouseDown}
                        onMouseEnter={handleMouseEnter}
                        onStartDrag={(e) => handleStartDrag(cell, rowIndex, colIndex, e)}
                        isPainting={isPainting}
                        onClearCell={handleClearCell}
                        palette={project.palette}
                        isSelected={
                          selectedTool === 'place-stitch'
                            ? false
                            : !isFabricPreview &&
                            selectedStitchCell?.row === rowIndex &&
                            selectedStitchCell?.col === colIndex
                        }
                        isBeingDragged={
                          draggedStitch &&
                          draggedStitch.row === rowIndex &&
                          draggedStitch.col === colIndex
                        }
                        previewColor={
                          selectedTool === 'place-stitch'
                            ? null
                            : inBrushPreview
                              ? selectedTool === 'erase'
                                ? 'rgba(220, 38, 38, 0.18)'
                                : selectedColor?.hex
                              : null
                        }
                        previewOutline={
                          selectedTool === 'place-stitch' ? false : inBrushPreview
                        }
                        previewModeType={
                          selectedTool === 'place-stitch'
                            ? null
                            : selectedTool === 'erase'
                              ? 'erase'
                              : 'paint'
                        }
                      />
                    )
                  })}
                </div>
              ))}

              {stitchPreviewEnabled &&
                cursorPreview.visible &&
                Number.isFinite(cursorPreview.x) &&
                Number.isFinite(cursorPreview.y) && (
                  <div
                    className="stitch-cursor-preview"
                    style={{
                      left: cursorPreview.x,
                      top: cursorPreview.y,
                      transform: `translate(-50%, -50%) rotate(${getStitchPreviewRotation(
                        selectedDirection
                      )}deg)`,
                    }}
                  >
                    <svg width="26" height="26" viewBox="0 0 24 24">
                      <path
                        d="M12 4 L12 20 M6 10 L12 4 L18 10"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                    </svg>
                  </div>
                )}

              {!isFabricPreview && (
                <GridOverlay
                  rows={project.grid.length}
                  cols={project.grid[0]?.length || 0}
                  spi={spi}
                />
              )}

              {selectedStitchCell && !isFabricPreview && (
                <StitchActionPad
                  top={manualPadPos?.top ?? stitchActionPadPos.top}
                  left={manualPadPos?.left ?? stitchActionPadPos.left}
                  onMoveUp={handleMoveUp}
                  onMoveDown={handleMoveDown}
                  onMoveLeft={handleMoveLeft}
                  onMoveRight={handleMoveRight}
                  onDuplicateUp={handleDuplicateUp}
                  onDuplicateDown={handleDuplicateDown}
                  onDuplicateLeft={handleDuplicateLeft}
                  onDuplicateRight={handleDuplicateRight}
                  onDelete={handleDeleteSelectedStitch}
                  onClose={handleCloseSelectedStitch}
                  onGrabMouseDown={handlePadGrabStart}
                />
              )}
            </div>

            {!isFabricPreview && (
              <div className="right-ruler" style={{ height: `${gridHeight}px` }}>
                {Array.from({ length: rows + 1 }).map((_, index) => {
                  const percent = (index / rows) * 100
                  const tickClass =
                    index % spi === 0 ? 'major' : index % halfSpi === 0 ? 'mid' : 'minor'

                  return (
                    <div
                      key={`right-${index}`}
                      className={`ruler-tick right ${tickClass}`}
                      style={{ top: `${percent}%` }}
                    >
                      {index % spi === 0 ? (
                        <span className="ruler-label">{index / spi}</span>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            )}

            {!isFabricPreview && <div className="ruler-corner" />}

            {!isFabricPreview && (
              <div className="bottom-ruler" style={{ width: `${gridWidth}px` }}>
                {Array.from({ length: cols + 1 }).map((_, index) => {
                  const percent = (index / cols) * 100
                  const tickClass =
                    index % spi === 0 ? 'major' : index % halfSpi === 0 ? 'mid' : 'minor'

                  return (
                    <div
                      key={`bottom-${index}`}
                      className={`ruler-tick bottom ${tickClass}`}
                      style={{ left: `${percent}%` }}
                    >
                      {index % spi === 0 ? (
                        <span className="ruler-label">{index / spi}</span>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            )}

            {!isFabricPreview && <div className="ruler-corner" />}
          </div>
        </div>

        {!isFabricPreview && showMiniMap && (
          <div className="mini-map-panel">
            <div className="mini-map-title">Mini-map</div>
            <div className="mini-map-body">
              <div className="mini-map-stage" onClick={handleMiniMapClick}>
                <div
                  className="mini-map-grid"
                  style={{
                    gridTemplateColumns: `repeat(${miniMapCols}, ${miniMapCellSize}px)`,
                    gridTemplateRows: `repeat(${miniMapRows}, ${miniMapCellSize}px)`,
                  }}
                >
                  {project.grid.flatMap((rowArr, rowIndex) =>
                    rowArr.map((cell, colIndex) => {
                      const fill =
                        project.palette?.find?.(
                          (p) => String(p?.code) === String(cell?.colorId)
                        )?.hex ||
                        project.palette?.find?.(
                          (p) => String(p?.code) === String(cell?.colorId)
                        )?.color ||
                        'transparent'

                      return (
                        <div
                          key={`mini-${rowIndex}-${colIndex}`}
                          className="mini-map-cell"
                          style={{
                            width: `${miniMapCellSize}px`,
                            height: `${miniMapCellSize}px`,
                            backgroundColor: fill,
                          }}
                        />
                      )
                    })
                  )}
                </div>

                <div
                  className="mini-map-viewport"
                  style={{
                    width: `${viewportMiniWidth}px`,
                    height: `${viewportMiniHeight}px`,
                    left: `${viewportMiniLeft}px`,
                    top: `${viewportMiniTop}px`,
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {renderDraggedStitch()}

      {!isFabricPreview && showBrushBadge && (
        <div
          style={{
            position: 'fixed',
            left: `${cursorPos.x + (isPainting ? 8 : 16)}px`,
            top: `${cursorPos.y + (isPainting ? 8 : 16)}px`,
            zIndex: 999999,
            pointerEvents: 'none',
          }}
        >
          <div
            key={brushBadgePulseKey}
            className="brush-size-badge pulse"
            style={{
              background: getBrushBadgeColor(),
              color: '#fff',
              fontSize: '11px',
              padding: '4px 6px',
              borderRadius: '6px',
              fontWeight: 600,
              letterSpacing: '0.3px',
            }}
          >
            {getBrushBadgeLabel()}
          </div>
        </div>
      )}
    </>
  )
}

export default DesignCanvas